<template>
	<header>
		<nav class="main fix-on-scroll" :class="page && page.headingImage ? '' : 'no-slider'">
			<div class="row" :class="{ active: showMenu }">
				<div class="columns column12 flex-row">
					<div v-if="showMenu" class="menu-button button" @click="hideMenu">
						<font-awesome-icon icon="fa-light fa-times" size="1x" />
					</div>
					<div v-else class="menu-button button" @click="toggleMenu">
						<font-awesome-icon icon="fa-light fa-bars" size="1x" />
						<span>{{ $t('menu') }}</span>
					</div>
					<button class="logo" aria-label="Back to homepage" @click="handleLogoClick" />
					<div class="nav-buttons">
						<book-button class="button button-cta reserve-now right">
							<font-awesome-icon icon="fa-light fa-calendar-days" size="1x" />
							<span>{{ defaults[locale].website.book }}</span>
						</book-button>
						<lang-select :class="{ 'active-mobile': showMenu }" :items="page.langmenu" />
					</div>
				</div>
			</div>
			<div class="nav-container" :class="{ active: showMenu }">
				<div class="row">
					<div class="inner-row">
						<ul class="main-nav">
							<li
								v-for="item in topmenu[locale]"
								:key="item.filename"
								:class="{ 'has-dropdown': item.submenu.length }"
							>
								<nuxt-link :exact="item.slug === 'index'" :to="`${item.filename}`" @click="hideMenu">
									{{ item.header }}
								</nuxt-link>
								<submenu
									v-if="item.submenu.length"
									:hide-all="!showMenu"
									:item="item"
									:subitems="item.submenu"
									@hide-menu="hideMenu"
								/>
							</li>
							<li v-if="page" class="lang-nav">
								<div v-for="item in page.langmenu" :key="`${item.filename}-${item.language}`">
									<nuxt-link
										class="flag"
										:class="item.language"
										:to="`${item.filename}`"
										@click="hideMenu"
									>
										<span>{{ $t(item.language) }}</span>
									</nuxt-link>
								</div>
							</li>
						</ul>
						<div class="vertical-border" />
						<div class="nav-contact">
							<h3>{{ $t('contact') }}</h3>
							<p>
								<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
									<span>{{ defaults[locale].website.phone }}</span> </a
								><br />
								<a :href="`mailto:${defaults[locale].website.email}`"
									><span>{{ defaults[locale].website.email }}</span></a
								>
							</p>
							<h3>{{ $t('address') }}</h3>
							<p>
								<span>{{ defaults[locale].website.street }} {{ defaults[locale].website.number }}</span
								><br />
								<span
									>{{ defaults[locale].website.postalCode }} {{ defaults[locale].website.city }}</span
								><br />
								<span>{{ defaults[locale].website.country }}</span
								><br />
							</p>
							<social-media :socials="socials" />
						</div>
					</div>
				</div>
			</div>
		</nav>
	</header>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, socials, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localeRoute = useLocaleRoute();
const route = useRoute();

await fetchPage();
await fetchDefaults();

const { data: topmenu } = await useWebsiteFetch('topmenu');

const showMenu = ref(false);

const toggleMenu = () => {
	showMenu.value = !showMenu.value;
	document.body.classList.add('locked');
};

const hideMenu = () => {
	showMenu.value = false;
	document.body.classList.remove('locked');
};

const handleLogoClick = () => {
	if (showMenu.value) {
		hideMenu();
	}

	if (route.name?.startsWith('index')) {
		window.scrollTo(0, 0);
	} else {
		const route = localeRoute('index');
		navigateTo(route.fullPath);
	}
};
</script>

<style lang="scss" scoped>
.logo {
	all: unset;
	cursor: pointer;
	background: url('~/assets/images/logo-metzlr-house-medium-white.png') no-repeat center center;
	background-size: 110px;
	width: 110px;
	height: 62px;
	position: absolute;
	left: 0;
	right: 0;
	top: -8px;
	margin: 0 auto;
	transition: all 0.3s ease-in-out;
	animation: fade-from-top 1s ease;
}

nav.main {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	padding: 32px 0;
	transition: all 0.3s ease-in-out;
	overflow: visible;
	border-bottom: 1px solid #fff;
	height: 113px;
}

nav .row {
	overflow: visible;
	z-index: 102;
	position: relative;
}

nav.main.scroll,
nav.main.no-slider {
	background: var(--primary-background-color);
	border-color: var(--primary-background-color);

	.menu-button,
	.lang-select {
		color: var(--body-color);
	}

	.logo {
		background: url('~/assets/images/logo-metzlr-house-medium-black.png') no-repeat center center;
		background-size: 110px;
	}
}

.reserve-now {
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	align-items: center;
	padding: 9px 20px 8px;

	svg {
		display: none;
	}
}

.menu-button {
	background: none;
	padding: 0;
	margin: 0;
	font-size: 16px;
	font-weight: 400;
	cursor: pointer;
	text-align: left;
	animation: fade-from-left 0.7s ease;
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	opacity: 0.85;
	min-width: auto;

	svg {
		font-size: 32px;
		margin: 0 10px 0 0;
	}

	&:hover {
		opacity: 1;
		background: none;
	}
}

nav .flex-row,
.nav-buttons {
	justify-content: space-between;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
}

.nav-buttons {
	gap: 12px;
	color: #fff;
	animation: fade-from-right 0.7s ease;

	a {
		color: #fff;
		text-decoration: none;
		margin: 0;
		opacity: 0.85;

		&.button,
		&:hover {
			opacity: 1;
		}
	}
}

.nav-container {
	background: var(--contentblock-background-color);
	padding: 100px 0 0;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 101;
	width: 100%;
	height: 100%;
	opacity: 0;
	transition: all 0.4s ease-in-out;
	transform: translate(0, 100%) matrix(1, 0, 0, 1, 0, 0);

	.row {
		display: flex;
		flex-flow: column nowrap;
		justify-content: space-between;
		align-items: center;
		height: 100%;
		border-top: 1px solid #fff;
		width: 100%;
		max-width: 100%;
	}
}

.nav-container.active {
	transform: matrix(1, 0, 0, 1, 0, 0);
	opacity: 1;
}

nav.main .active {
	.logo {
		visibility: hidden;
	}

	.lang-select {
		position: absolute;
		right: 20px;
		top: -5px;
		color: var(--primary-background-color);
	}

	.reserve-now {
		position: absolute;
		right: 120px;
		top: -8px;
		color: var(--cta-color);
		background: #fff;
	}

	.menu-button {
		position: absolute;
		left: 20px;
		top: -10px;
		color: var(--primary-background-color);

		&:hover {
			color: #fff;
		}

		svg {
			font-size: 40px;
			margin-top: 10px;
		}
	}
}

.lang-nav {
	display: none;
	list-style: none;
	text-align: left;
	line-height: 26px;
	padding-top: 40px;

	a {
		color: #fff;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: center;
		font-size: 16px !important;
		gap: 5px;

		&.router-link-exact-active,
		&:hover {
			color: var(--cta-color);
		}
	}

	img {
		max-width: 16px;
		float: left;
		margin: 0 5px 0 0;
	}
}

.mobile-lang-nav {
	margin: 40px 0 0;
	font-size: 14px;
	display: none;
}

.mobile-lang-nav img {
	max-width: 16px;
	margin: 0 10px 0 0;
}

.main-nav .mobile-lang-nav a {
	width: 100%;
	display: block;
	font-size: 16px;
	line-height: 28px;
}

/* Fullscreen Nav */
.inner-row {
	width: 98%;
	text-align: left;
	max-width: 1480px;
	padding: 100px 20px 0;
	display: flex;
	flex-flow: row wrap;
	justify-content: space-between;
	align-items: center;
}

.main-nav {
	list-style: none;
	width: 66%;
	text-align: left;

	a {
		color: rgb(235 225 208 / 40%);
		font-size: 40px;
		line-height: 60px;
		font-weight: 200;
		font-family: var(--heading-font-family);

		&:hover,
		&.active,
		&.router-link-active {
			color: var(--primary-background-color);
		}
	}
}

.nav-contact {
	align-self: flex-end;
	width: 34%;
	display: flex;
	flex-flow: column wrap;
	align-items: flex-start;
	justify-content: flex-start;
	gap: 20px;
	padding: 20px 0 0;
	color: var(--primary-background-color);

	h3,
	a {
		color: var(--primary-background-color);
	}

	:deep(.socials) {
		a {
			color: var(--primary-background-color);
			border-color: var(--primary-background-color);
		}
	}

	svg {
		margin-right: 5px;
	}

	.socials {
		margin: 0;
	}
}

.nav-contact > a i {
	margin: 0 10px 0 0;
}

.vertical-border {
	width: 1px;
	height: 100%;
	background: #fff;
	position: absolute;
	inset: 0 calc(42% + 40px) 0 auto;
}

.close-wrapper {
	align-self: flex-start;
}

.nav-close {
	color: #fff;
	cursor: pointer;
	font-size: 54px;
	padding: 0 0 0 12px;
}

/* Breadcrumbs */
.breadcrumbs ul {
	list-style: none;
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: center;
	margin: 0 0 75px;
}

.breadcrumbs li {
	margin: 0 10px 0 0;
}

.breadcrumbs i {
	font-size: 11px;
}

.breadcrumbs a {
	color: var(--body-color);
}

.breadcrumbs a.active {
	color: var(--cta-color);
}

@media (max-height: 740px) {
	.nav-container {
		overflow-y: scroll;
	}

	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		z-index: 1;
	}

	.main-nav {
		z-index: 2;
		position: relative;
	}
}

@media (max-width: 920px) {
	.nav-buttons a,
	.menu-button span {
		display: none;
	}

	.menu-button {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 48px;
		height: 48px;
		line-height: 48px;
		padding: 0;
	}

	.reserve-now svg {
		display: none;
	}

	.nav-buttons .lang-select {
		display: none;

		&.active-mobile {
			display: block;
		}
	}

	.nav-buttons .reserve-now {
		min-width: auto;
		display: flex;
		padding: 7px 16px;
		letter-spacing: 2px;
		font-size: 9px;
	}

	.main-nav {
		width: 100%;
	}

	.inner-row {
		padding: 100px 40px 0;
	}

	.vertical-border {
		display: none;
	}

	.nav-contact {
		width: 100%;
		position: absolute;
		inset: auto 40px 0;

		h3,
		p {
			display: none;
		}
	}

	nav.main .active {
		.reserve-now {
			visibility: hidden;
			background: var(--cta-color);
			color: #fff;
		}
	}

	.close-wrapper {
		align-self: flex-end;
	}
}

@media (max-width: 800px) {
	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		right: auto;
		float: left;
		margin-top: 30px;
	}

	.main-nav {
		a {
			font-size: 30px;
			line-height: 45px;
		}
	}
}

@media (max-width: 600px) {
	.nav-container {
		padding: 60px 0 25px;

		.row {
			border: none;
		}
	}

	.main-nav,
	.contact-info,
	.lang-nav {
		width: 100%;
	}

	nav.main {
		.flex-row {
			padding: 0;
		}
	}

	.contact-info {
		margin: 25px auto 0;
	}

	.lang-nav {
		margin-top: 0;
	}

	nav .buttons .button {
		svg {
			margin-right: 0;
			font-size: 16px;
		}

		span {
			display: none;
		}
	}
}
</style>
